<script setup lang="ts">
import { useElementEmptiness } from '#composables/useElementEmptiness';
import { computed, ref, watchEffect } from 'vue';
import VueFeather from 'vue-feather';
import { type RouteLocationRaw } from 'vue-router';
import { getComponentType, getLinkProps } from './card.utils';

const {
  to = undefined,
  divide,
  vertical,
  hideLinkIcon,
  linkPosition = 'top',
  showEmpty
} = defineProps<{
  to?: RouteLocationRaw;
  divide?: boolean;
  vertical?: boolean;
  hideLinkIcon?: boolean;
  linkPosition?: 'top' | 'bottom';
  showEmpty?: boolean;
}>();

const component = ref(getComponentType(to));
const linkProps = ref(getLinkProps(to));

watchEffect(() => {
  component.value = getComponentType(to);
  linkProps.value = getLinkProps(to);
});

// --- Compute classes based on props.
const classes = computed(() => ({
  'hover:shadow-main cursor-pointer duration-100 ease active:translate-y-1 active:shadow-none': to,
  'divide-y divide-default-very-light': divide,
  'flex-col': vertical,
  'flex-col lg:flex-row': !vertical,
  'lg:divide-x lg:divide-y-0': divide && !vertical
}));

// --- Hide the card container if the container innerHTML is empty.
// --- Effectively hiding the entire component when there is no content.
const container = ref<HTMLElement | null>(null);

const content = ref<HTMLElement | null>(null);
const isContentEmpty = useElementEmptiness(content);

defineExpose({ container });
</script>

<template>
  <Component
    :is="component"
    v-if="!isContentEmpty || showEmpty"
    ref="container"
    v-bind="linkProps"
    class="flex relative grow w-full rounded-sm bg-white border flex-wrap border-default-very-light"
  >
    <!-- Link icon -->
    <VueFeather
      is="div"
      v-if="to && !hideLinkIcon"
      type="arrow-right"
      class="absolute right-6 text-primary p-2 rounded-bl"
      :class="{ 'top-6': linkPosition === 'top', 'bottom-6': linkPosition === 'bottom' }"
      aria-hidden="true"
    />

    <!-- Container -->
    <div ref="content" :class="classes" class="flex w-full h-full">
      <slot />
    </div>
  </Component>
</template>
